import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Button, Modal } from "react-bootstrap";
import * as XLSX from "xlsx";
import { ButtonLoader, LoadingComponent } from "Components";
import { newAuthService } from "Service/Axios";
import { createGlobalDataUrl, updateGlobalDataUrl } from "Constant/api";
import { useGlobalDataList } from "Constant/apiHooks";
import { Validator } from "Utils/validator";

export const MasterFieldModal = ({
  show,
  modalType,
  toggle,
  selectedData,
  fetchList,
}) => {
  const { t } = useTranslation();

  const { loading: globalDataLoading, globalDataList } = useGlobalDataList();

  const [loading, setLoading] = useState(false);
  const [parentKeyOptions, setParentKeyOptions] = useState([]);
  const [formData, setFormData] = useState({
    fieldName: "",
    fieldType: "",
    feildDescription: "",
    isParentKeyRequired: "N",
    parentFieldGid: "",
    parentFieldName: "",
    fieldImportFile: null,
    fieldDetails: [],
  });
  const [errMsg, setErrMsg] = useState({
    fieldName: "",
    fieldType: "",
    feildDescription: "",
  });

  const {
    fieldName,
    fieldType,
    feildDescription,
    isParentKeyRequired,
    parentFieldGid,
    parentFieldName,
    fieldImportFile,
    fieldDetails,
  } = formData;

  const validationRequiredFields = [
    "fieldName",
    "fieldType",
    "feildDescription",
  ];

  // On Change
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "parentFieldGid") {
      const selectedField = globalDataList.find((item) => item.gid === value);
      setFormData((prev) => ({
        ...prev,
        parentFieldGid: value,
        parentFieldName: selectedField?.name || "",
      }));
      setParentKeyOptions(
        selectedField?.details?.map((entry) => ({
          key: entry.key,
          value: entry.value,
        })) || []
      );

      // Conditional validation for parent field
      if (isParentKeyRequired === "Y" && !Validator.text(value)) {
        setErrMsg((prev) => ({
          ...prev,
          parentFieldGid: t(
            "common.messages.error.masterData.parentFieldGid.blank"
          ),
        }));
      } else {
        setErrMsg((prev) => ({
          ...prev,
          parentFieldGid: "",
        }));
      }
    } else if (name === "isParentKeyRequired") {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        parentFieldGid: "",
        parentFieldName: "",
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    if (validationRequiredFields.includes(name)) {
      if (!Validator.text(value)) {
        setErrMsg((prev) => ({
          ...prev,
          [name]: t(`common.messages.error.masterData.${name}.blank`),
        }));
      } else {
        setErrMsg((prev) => ({
          ...prev,
          [name]: "",
        }));
      }
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData((prev) => ({ ...prev, fieldImportFile: file }));

    const reader = new FileReader();

    reader.onload = (event) => {
      const workbook = XLSX.read(event.target.result, { type: "binary" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const sheetData = XLSX.utils.sheet_to_json(sheet);

      const updatedData = sheetData.map((row) => ({
        key: row["Key"] || "",
        value: row["Value"] || "",
        parentkey: row["Parent Key"]
          ? row["Parent Key"].split(",").map((pk) => pk.trim())
          : [],
      }));

      setFormData((prev) => ({
        ...prev,
        fieldDetails: updatedData,
      }));
    };

    reader.readAsArrayBuffer(file);
  };

  const handleFileRemove = () => {
    setFormData((prev) => ({ ...prev, fieldImportFile: null }));
  };

  useEffect(() => {
    if (selectedData) {
      setFormData({
        fieldName: selectedData.name,
        fieldType: selectedData.type,
        feildDescription: selectedData.description,
        isParentKeyRequired: selectedData.parentFieldGid ? "Y" : "N",
        parentFieldGid: selectedData.parentFieldGid || "",
        parentFieldName: selectedData.parentFieldName || "",
      });
    }
  }, [selectedData]);

  const getValidation = () => {
    return new Promise((resolve) => {
      let isValid = true;
      const errors = {};

      // Validate always-required fields
      for (const field of validationRequiredFields) {
        const value = formData[field];
        if (!Validator.text(value)) {
          errors[field] = t(`common.messages.error.masterData.${field}.blank`);
          isValid = false;
        }
      }

      // Conditional validation for parentFieldGid
      if (isParentKeyRequired === "Y") {
        if (!Validator.text(parentFieldGid)) {
          errors.parentFieldGid = t(
            "common.messages.error.masterData.parentFieldGid.blank"
          );
          isValid = false;
        }
      }

      // Set error messages
      setErrMsg((prev) => ({
        ...prev,
        ...errors,
      }));

      resolve(isValid);
    });
  };

  // On Submit
  const onSaveField = async () => {
    const validationStatus = await getValidation();
    if (validationStatus) {
      setLoading(true);
      try {
        const payload = {
          name: fieldName,
          type: fieldType,
          description: feildDescription,
          parentFieldGid: parentFieldGid || "",
          parentFieldName: parentFieldName || "",
        };

        if (modalType === "import" && fieldDetails.length > 0) {
          payload.details = fieldDetails;
        }

        if (selectedData) {
          payload.gid = selectedData.gid;
          const response = await newAuthService(
            updateGlobalDataUrl,
            "POST",
            payload
          );
          if (response.status) {
            toast.success(
              t("common.messages.notification.success.updateField")
            );
          } else {
            toast.error(response.message);
          }
        } else {
          const response = await newAuthService(
            createGlobalDataUrl,
            "POST",
            payload
          );
          if (response.status) {
            toast.success(
              t("common.messages.notification.success.createField")
            );
          } else {
            toast.error(response.message);
          }
        }
        setLoading(false);
        fetchList();
        toggle();
      } catch (error) {
        setLoading(false);
        if (error.response && error.response.data) {
          toast.error(error.response.data.message);
        }
      }
    }
  };

  return (
    <Modal
      show={show}
      onHide={toggle}
      centered
      backdrop="static"
      keyboard={false}
      size={modalType === "import" ? "lg" : "md"}
    >
      <Modal.Header className="">
        <Modal.Title className="text-md">Add Field</Modal.Title>
      </Modal.Header>
      <Modal.Body className="min-h-250">
        <LoadingComponent isLoading={globalDataLoading}>
          <div className="row">
            <div className={`col-md-6 ${errMsg.fieldName ? "error" : ""}`}>
              <div className="mb-3">
                <label htmlFor="fieldName" className="form-label">
                  Field Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter field name"
                  id="fieldName"
                  name="fieldName"
                  value={fieldName}
                  onChange={handleChange}
                />
                {errMsg.fieldName && <p>{errMsg.fieldName}</p>}
              </div>
            </div>
            <div className={`col-md-6 ${errMsg.fieldType ? "error" : ""}`}>
              <div className="mb-3">
                <label htmlFor="fieldType" className="form-label">
                  Field Type <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter field type"
                  id="fieldType"
                  name="fieldType"
                  value={fieldType}
                  onChange={handleChange}
                />
                {errMsg.fieldType && <p>{errMsg.fieldType}</p>}
              </div>
            </div>
            <div className={`col-12 ${errMsg.feildDescription ? "error" : ""}`}>
              <div className="mb-3">
                <label htmlFor="feildDescription" className="form-label">
                  Field Description <span className="text-danger">*</span>
                </label>
                <textarea
                  className="form-control"
                  placeholder="Enter field description"
                  id="feildDescription"
                  name="feildDescription"
                  value={feildDescription}
                  onChange={handleChange}
                  rows={3}
                />
                {errMsg.feildDescription && <p>{errMsg.feildDescription}</p>}
              </div>
            </div>
            {globalDataList && globalDataList.length > 0 && (
              <>
                <div className="col-12">
                  <div className="mb-3">
                    <label className="form-label me-2">
                      Is Parent Key Required
                    </label>
                    <input
                      type="radio"
                      className="form-check-input me-2"
                      name="isParentKeyRequired"
                      value="Y"
                      checked={formData.isParentKeyRequired === "Y"}
                      onChange={handleChange}
                    />
                    <span className="me-3">Yes</span>
                    <input
                      type="radio"
                      className="form-check-input me-2"
                      name="isParentKeyRequired"
                      value="N"
                      checked={formData.isParentKeyRequired === "N"}
                      onChange={handleChange}
                    />
                    <span>No</span>
                  </div>
                </div>
                {isParentKeyRequired === "Y" && (
                  <div
                    className={`col-12 ${errMsg.parentFieldGid ? "error" : ""}`}
                  >
                    <div className="mb-3">
                      <label className="form-label me-2" htmlFor="parentFieldGid">
                        Parent Field
                        <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        id="parentFieldGid"
                        name="parentFieldGid"
                        value={parentFieldGid}
                        onChange={handleChange}
                        disabled={globalDataLoading}
                      >
                        <option value="">Select Parent Field</option>
                        {globalDataList
                          .filter((field) => field.gid !== selectedData?.gid)
                          .map((field) => (
                            <option key={field.gid} value={field.gid}>
                              {field.name}
                            </option>
                          ))}
                      </select>
                      {errMsg.parentFieldGid && <p>{errMsg.parentFieldGid}</p>}
                    </div>
                  </div>
                )}
              </>
            )}

            {parentFieldGid.trim() !== "" && modalType === "import" && (
              <>
                <div className="col-md-12">
                  <div className="mb-3">
                    <div className="p-4 rounded-lg border border-primary-200 bg-primary-50">
                      <p className="text-primary-800 mb-2">
                        <strong>Note:</strong> Please upload an Excel file
                        containing the following columns:
                      </p>

                      <ul className="mb-2">
                        <li>
                          <strong>Key</strong> - unique identifier for the entry.
                        </li>
                        <li>
                          <strong>Value</strong> - description or display value for
                          the key.
                        </li>
                        <li>
                          <strong>Parent Key</strong> - (optional) one or more
                          parent keys separated by commas. Each parent key must
                          match one of the available values shown in the reference
                          list below.
                        </li>
                      </ul>

                      <details>
                        <summary className="font-semibold text-primary-800">
                          View Parent Key Reference List
                        </summary>
                        <div
                          style={{
                            maxHeight: "120px",
                            overflowY: "auto",
                            marginTop: "8px",
                          }}
                        >
                          {parentKeyOptions.map((item, index) => (
                            <div key={index}>{item.key}</div>
                          ))}
                        </div>
                      </details>
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Upload Excel File <span className="text-danger">*</span></label>
                  {fieldImportFile ? (
                    <div className="">
                      <div className="form-control py-0-5 pr-0-5 d-flex align-items-center justify-content-between">
                        <span className="">
                          {fieldImportFile.name} ({fieldImportFile.size})
                        </span>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger-light ms-2 w-7 h-7 p-0 d-flex justify-content-center align-items-center"
                          onClick={handleFileRemove}
                        >
                          <i className="bx bx-trash"></i>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <input
                      type="file"
                      className="form-control"
                      name="fieldImportFile"
                      accept=".xls,.xlsx, .csv"
                      onChange={handleFileChange}
                    />
                  )}
                </div>
              </>
            )}
          </div>
        </LoadingComponent>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={toggle}>
          {t("common.button.close")}
        </Button>
        {!loading ? (
          <Button variant="primary" onClick={onSaveField}>
            {t("common.button.save")}
          </Button>
        ) : (
          <ButtonLoader className="btn-loading-sm" />
        )}
      </Modal.Footer>
    </Modal>
  );
};
